import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "antd";
import { toast } from "react-toastify";
import { hasRequiredRole } from "../../utils/auth";
import SecureDeleteConfirmation from "./SecureDeleteConfirmation";
import SecureDeleteOtp from "./SecureDeleteOtp";
import "./secureDelete.scss";

export const SECURE_DELETE_STEPS = {
  CONFIRMATION: "CONFIRMATION",
  OTP_VERIFICATION: "OTP_VERIFICATION",
};

/**
 * Reusable secure deletion flow:
 * Confirmation (type entity name) → Send OTP → Verify OTP → Delete → Success
 *
 * Configure via props so Partners, Organisations, Apps, etc. can reuse it.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.entityType - e.g. "Partner"
 * @param {string|number} props.entityId
 * @param {string} props.displayName - exact name the admin must type
 * @param {string} [props.requiredRole="SUPER_ADMIN"]
 * @param {string} [props.warningMessage]
 * @param {string} [props.subWarningMessage]
 * @param {number} [props.otpLength=6]
 * @param {() => Promise<any>} props.sendOtp
 * @param {(otp: string) => Promise<any>} props.verifyOtp
 * @param {(verificationResult: any) => Promise<any>} props.deleteAction
 * @param {() => void} [props.onSuccess]
 * @param {string} [props.successMessage]
 */
export default function SecureDeleteFlow({
  open,
  onClose,
  entityType = "item",
  entityId,
  displayName = "",
  requiredRole = "SUPER_ADMIN",
  warningMessage,
  subWarningMessage,
  otpLength = 6,
  sendOtp,
  verifyOtp,
  deleteAction,
  onSuccess,
  successMessage,
}) {
  const [step, setStep] = useState(SECURE_DELETE_STEPS.CONFIRMATION);
  const [sendLoading, setSendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [challengeNonce, setChallengeNonce] = useState(0);

  const isBusy = sendLoading || verifyLoading || resendLoading;

  const resetFlow = useCallback(() => {
    setStep(SECURE_DELETE_STEPS.CONFIRMATION);
    setSendLoading(false);
    setVerifyLoading(false);
    setResendLoading(false);
    setOtpError("");
  }, []);

  useEffect(() => {
    if (open) {
      resetFlow();
      setChallengeNonce((prev) => prev + 1);
    }
  }, [open, entityId, displayName, resetFlow]);

  const handleClose = () => {
    if (isBusy) return;
    resetFlow();
    onClose?.();
  };

  const handleSendOtp = async ({ isResend = false } = {}) => {
    if (!hasRequiredRole(requiredRole)) {
      toast.error("You are not authorized to perform this action.");
      return;
    }

    if (typeof sendOtp !== "function") {
      toast.error("Secure delete is not configured correctly.");
      return;
    }

    if (isResend) {
      setResendLoading(true);
    } else {
      setSendLoading(true);
    }
    setOtpError("");

    try {
      await sendOtp();
      setStep(SECURE_DELETE_STEPS.OTP_VERIFICATION);
      if (isResend) {
        toast.success("A new verification code has been sent.");
      }
    } catch (error) {
      const message =
        error?.message ||
        error?.error ||
        error?.data?.message ||
        "Unable to send verification code. Please try again.";
      toast.error(message);
      // Stay on confirmation (or OTP if resend failed)
    } finally {
      setSendLoading(false);
      setResendLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    if (!hasRequiredRole(requiredRole)) {
      toast.error("You are not authorized to perform this action.");
      return;
    }

    if (typeof verifyOtp !== "function" || typeof deleteAction !== "function") {
      toast.error("Secure delete is not configured correctly.");
      return;
    }

    setVerifyLoading(true);
    setOtpError("");

    try {
      const verificationResult = await verifyOtp(otp);

      // Never delete unless OTP verification explicitly succeeded (boolean true)
      if (verificationResult?.verified !== true) {
        const message =
          verificationResult?.message ||
          "Invalid OTP. Please check the code and try again.";
        setOtpError(message);
        return;
      }

      try {
        await deleteAction(verificationResult);
        toast.success(
          successMessage || `${entityType} deleted successfully.`
        );
        resetFlow();
        onClose?.();
        onSuccess?.();
      } catch (deleteError) {
        const message =
          deleteError?.response?.data?.message ||
          deleteError?.response?.data?.error ||
          deleteError?.message ||
          deleteError?.error ||
          `Unable to delete the ${entityType}. Please try again.`;
        toast.error(typeof message === "string" ? message : String(message));
      }
    } catch (error) {
      const message =
        error?.message ||
        error?.error ||
        error?.data?.message ||
        "Invalid OTP. Please check the code and try again.";
      setOtpError(message);
    } finally {
      setVerifyLoading(false);
    }
  };

  if (!hasRequiredRole(requiredRole) && open) {
    // Defensive: do not render the flow for unauthorized roles
    return null;
  }

  return (
    <Modal
      className="secure-delete-modal"
      open={open}
      onCancel={handleClose}
      footer={false}
      closable={!isBusy}
      maskClosable={!isBusy}
      keyboard={!isBusy}
      destroyOnClose
    >
      {step === SECURE_DELETE_STEPS.CONFIRMATION ? (
        <SecureDeleteConfirmation
          key={challengeNonce}
          entityType={entityType}
          displayName={displayName}
          warningMessage={warningMessage}
          subWarningMessage={subWarningMessage}
          loading={sendLoading}
          onCancel={handleClose}
          onContinue={() => handleSendOtp({ isResend: false })}
        />
      ) : (
        <SecureDeleteOtp
          entityType={entityType}
          otpLength={otpLength}
          loading={verifyLoading}
          resendLoading={resendLoading}
          errorMessage={otpError}
          onCancel={handleClose}
          onVerify={handleVerifyOtp}
          onResend={() => handleSendOtp({ isResend: true })}
        />
      )}
    </Modal>
  );
}
